import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Grid, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Pagination, Stack, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Avatar, useMediaQuery,
  FormControlLabel, Checkbox, alpha, Divider,
  List, ListItem, ListItemAvatar, ListItemText, SwipeableDrawer
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  PersonAdd as PersonAddIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import Layout from '../components/Layout';
import StatsCard from '../components/StatsCard';
import PageHeader from '../components/PageHeader';

// Funções auxiliares
const maskCPF = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const maskCNPJ = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const maskPhone = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
};

const maskCEP = (value) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{3})\d+?$/, '$1');
};

const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const validateCPF = (cpf) => {
  cpf = cpf.replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf.charAt(i)) * (10 - i);
  let rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9))) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i)) * (11 - i);
  rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(10))) return false;
  return true;
};

const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/\D/g, '');
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;
  let size = cnpj.length - 2;
  let numbers = cnpj.substring(0, size);
  const digits = cnpj.substring(size);
  let sum = 0;
  let pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  size = size + 1;
  numbers = cnpj.substring(0, size);
  sum = 0;
  pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  return true;
};

function Clientes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nome_razao: '',
    cpf_cnpj: '',
    tipo_pessoa: 'FISICA',
    telefone1: '',
    telefone2: '',
    email: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    observacoes: '',
    ativo: true
  });

  const [filters, setFilters] = useState({
    busca: '',
    ativo: 'true'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    perPage: isMobile ? 5 : 10
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, nome: '' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);

  const totalClientes = clientes.length;
  const ativos = clientes.filter(c => c.ativo).length;
  const inativos = totalClientes - ativos;

  useEffect(() => {
    carregarClientes();
  }, [pagination.page, filters.busca, filters.ativo]);

  const carregarClientes = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.perPage,
        busca: filters.busca || undefined,
        ativo: filters.ativo === '' ? undefined : filters.ativo === 'true'
      };

      const response = await api.get('/clientes', { params });

      if (Array.isArray(response.data)) {
        setClientes(response.data);
        setPagination(prev => ({ ...prev, total: response.data.length }));
      } else {
        setClientes(response.data.clientes || response.data.rows || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || response.data.count || 0
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    carregarClientes();
  };

  const handleClearFilters = () => {
    setFilters({ busca: '', ativo: 'true' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setEditingId(cliente.id);
      setFormData({
        nome_razao: cliente.nome_razao || '',
        cpf_cnpj: cliente.cpf_cnpj || '',
        tipo_pessoa: cliente.tipo_pessoa || 'FISICA',
        telefone1: cliente.telefone1 || '',
        telefone2: cliente.telefone2 || '',
        email: cliente.email || '',
        cep: cliente.cep || '',
        endereco: cliente.endereco || '',
        numero: cliente.numero || '',
        complemento: cliente.complemento || '',
        bairro: cliente.bairro || '',
        cidade: cliente.cidade || '',
        estado: cliente.estado || '',
        observacoes: cliente.observacoes || '',
        ativo: cliente.ativo !== undefined ? cliente.ativo : true
      });
    } else {
      setEditingId(null);
      setFormData({
        nome_razao: '',
        cpf_cnpj: '',
        tipo_pessoa: 'FISICA',
        telefone1: '',
        telefone2: '',
        email: '',
        cep: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        observacoes: '',
        ativo: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCpfCnpjChange = (e) => {
    const value = e.target.value;
    const masked = formData.tipo_pessoa === 'FISICA' ? maskCPF(value) : maskCNPJ(value);
    setFormData(prev => ({ ...prev, cpf_cnpj: masked }));
  };

  const handlePhoneChange = (e, field) => {
    const value = maskPhone(e.target.value);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCepChange = async (e) => {
    const cep = maskCEP(e.target.value);
    setFormData(prev => ({ ...prev, cep }));

    if (cep.length === 9) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`);
        const data = await response.json();

        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            endereco: data.logradouro || '',
            bairro: data.bairro || '',
            cidade: data.localidade || '',
            estado: data.uf || ''
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const validateForm = () => {
    if (!formData.nome_razao.trim()) {
      toast.error('Nome/Razão Social é obrigatório');
      return false;
    }

    if (formData.email && !validateEmail(formData.email)) {
      toast.error('Email inválido');
      return false;
    }

    if (formData.cpf_cnpj) {
      const clean = formData.cpf_cnpj.replace(/\D/g, '');
      if (formData.tipo_pessoa === 'FISICA' && clean.length === 11 && !validateCPF(clean)) {
        toast.error('CPF inválido');
        return false;
      }
      if (formData.tipo_pessoa === 'JURIDICA' && clean.length === 14 && !validateCNPJ(clean)) {
        toast.error('CNPJ inválido');
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        cpf_cnpj: formData.cpf_cnpj?.replace(/\D/g, '') || null,
        telefone1: formData.telefone1?.replace(/\D/g, '') || null,
        telefone2: formData.telefone2?.replace(/\D/g, '') || null,
        cep: formData.cep?.replace(/\D/g, '') || null
      };

      if (editingId) {
        await api.put(`/clientes/${editingId}`, dataToSend);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/clientes', dataToSend);
        toast.success('Cliente criado com sucesso!');
      }

      handleCloseDialog();
      carregarClientes();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id, nome) => {
    setDeleteDialog({ open: true, id, nome });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/clientes/${deleteDialog.id}`);
      toast.success('Cliente excluído com sucesso!');
      carregarClientes();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      toast.error(error.response?.data?.error || 'Erro ao excluir cliente');
    } finally {
      setDeleteDialog({ open: false, id: null, nome: '' });
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/clientes/${id}/toggle-status`, { ativo: !currentStatus });
      toast.success(`Cliente ${currentStatus ? 'inativado' : 'ativado'} com sucesso!`);
      carregarClientes();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status');
    }
  };

  const handleClienteClick = (cliente) => {
    if (isMobile) {
      setSelectedCliente(cliente);
      setDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedCliente(null);
  };

  return (
    <Layout title="Clientes">
      <PageHeader
        title="Clientes"
        subtitle={`${totalClientes} clientes cadastrados • ${ativos} ativos`}
        action={() => handleOpenDialog()}
        actionText={isMobile ? 'Novo' : 'Novo Cliente'}
        icon={<PeopleIcon sx={{ color: '#39FF14' }} />}
      />

      {/* Cards de Estatísticas */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Total"
            value={totalClientes}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Ativos"
            value={ativos}
            icon={<PersonIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Inativos"
            value={inativos}
            icon={<PersonIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Hoje"
            value={clientes.filter(c => {
              const hoje = new Date().toDateString();
              const criado = new Date(c.createdAt).toDateString();
              return criado === hoje;
            }).length}
            icon={<AddIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      <Card sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 3 }}>
        {/* Filtros */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Buscar cliente"
                name="busca"
                value={filters.busca}
                onChange={handleFilterChange}
                placeholder="Nome, email ou telefone"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filters.busca && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFilters({ ...filters, busca: '' })}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'background.paper'
                  }
                }}
              />
            </Grid>
            <Grid item xs={8} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="ativo"
                  value={filters.ativo}
                  label="Status"
                  onChange={handleFilterChange}
                  sx={{ borderRadius: 3 }}
                >
                  <MenuItem value="true">Ativos</MenuItem>
                  <MenuItem value="false">Inativos</MenuItem>
                  <MenuItem value="">Todos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                sx={{
                  borderRadius: 3,
                  py: 1,
                  bgcolor: '#0A2647',
                  '&:hover': { bgcolor: '#1a3b5c' }
                }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Lista Mobile vs Tabela Desktop */}
        {isMobile ? (
          // VERSÃO MOBILE - Lista em Cards
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : clientes.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Nenhum cliente encontrado
              </Alert>
            ) : (
              <AnimatePresence>
                {clientes.map((cliente, index) => (
                  <motion.div
                    key={cliente.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      sx={{ 
                        mb: 1.5, 
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.06)',
                        '&:active': { transform: 'scale(0.98)' }
                      }}
                      onClick={() => handleClienteClick(cliente)}
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ 
                              bgcolor: alpha('#0A2647', 0.1),
                              color: '#0A2647',
                              width: 40,
                              height: 40
                            }}>
                              {cliente.nome_razao?.charAt(0)?.toUpperCase() || 'C'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                                {cliente.nome_razao}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cliente.email || 'Sem email'}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={cliente.ativo ? 'Ativo' : 'Inativo'}
                            size="small"
                            color={cliente.ativo ? 'success' : 'default'}
                            sx={{ 
                              borderRadius: 2, 
                              fontWeight: 500,
                              ...(cliente.ativo && {
                                bgcolor: alpha('#2ecc71', 0.15),
                                color: '#1a7a3a'
                              })
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                          <Box display="flex" gap={2}>
                            {cliente.telefone1 && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption">{cliente.telefone1}</Typography>
                              </Box>
                            )}
                            {cliente.cpf_cnpj && (
                              <Typography variant="caption" color="text.secondary">
                                {cliente.cpf_cnpj}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(cliente);
                              }}
                              sx={{ color: '#0A2647' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleStatus(cliente.id, cliente.ativo);
                              }}
                              color={cliente.ativo ? 'warning' : 'success'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}

            {/* Paginação Mobile */}
            {pagination.total > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(pagination.total / pagination.perPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      fontSize: '0.75rem'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          // VERSÃO DESKTOP - Tabela
          <>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#0A2647', 0.04) }}>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>CPF/CNPJ</strong></TableCell>
                    <TableCell><strong>Contato</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : clientes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Nenhum cliente encontrado
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    clientes.map((cliente) => (
                      <TableRow 
                        key={cliente.id} 
                        hover
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha('#0A2647', 0.02)
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ 
                              bgcolor: alpha('#0A2647', 0.1),
                              color: '#0A2647',
                              width: 36,
                              height: 36
                            }}>
                              {cliente.nome_razao?.charAt(0)?.toUpperCase() || 'C'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {cliente.nome_razao}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cliente.email || 'Sem email'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {cliente.cpf_cnpj || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cliente.tipo_pessoa === 'FISICA' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {cliente.telefone1 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2">{cliente.telefone1}</Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cliente.ativo ? 'Ativo' : 'Inativo'}
                            size="small"
                            color={cliente.ativo ? 'success' : 'default'}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 600,
                              ...(cliente.ativo && {
                                bgcolor: alpha('#2ecc71', 0.15),
                                color: '#1a7a3a'
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(cliente)}
                            sx={{
                              color: '#0A2647',
                              '&:hover': { bgcolor: alpha('#0A2647', 0.08) }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(cliente.id, cliente.ativo)}
                            color={cliente.ativo ? 'warning' : 'success'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação Desktop */}
            {pagination.total > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(pagination.total / pagination.perPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Card>

      {/* Drawer Mobile - Detalhes do Cliente */}
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onOpen={() => {}}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '80vh',
            p: 2
          }
        }}
      >
        {selectedCliente && (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2,
              pb: 1,
              borderBottom: '1px solid rgba(0,0,0,0.06)'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
                {selectedCliente.nome_razao}
              </Typography>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">CPF/CNPJ</Typography>
                    <Typography variant="body2">{selectedCliente.cpf_cnpj || 'Não informado'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Telefone</Typography>
                    <Typography variant="body2">{selectedCliente.telefone1 || 'Não informado'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2">{selectedCliente.email || 'Não informado'}</Typography>
                  </Box>
                </Box>
              </Paper>

              {selectedCliente.endereco && (
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Endereço</Typography>
                      <Typography variant="body2">
                        {selectedCliente.endereco}
                        {selectedCliente.numero && `, ${selectedCliente.numero}`}
                        {selectedCliente.bairro && ` - ${selectedCliente.bairro}`}
                        {selectedCliente.cidade && `, ${selectedCliente.cidade}`}
                        {selectedCliente.estado && `-${selectedCliente.estado}`}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    handleCloseDrawer();
                    handleOpenDialog(selectedCliente);
                  }}
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: '#0A2647',
                    color: '#ffffff',
                    '&:hover': { 
                      bgcolor: '#1a3b5c',
                      color: '#ffffff'
                    }
                  }}
                >
                  Editar
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color={selectedCliente.ativo ? 'warning' : 'success'}
                  onClick={() => {
                    handleToggleStatus(selectedCliente.id, selectedCliente.ativo);
                    handleCloseDrawer();
                  }}
                  sx={{ 
                    borderRadius: 2,
                    color: selectedCliente.ativo ? '#ed6c02' : '#2e7d32',
                    borderColor: selectedCliente.ativo ? '#ed6c02' : '#2e7d32',
                    '&:hover': {
                      borderColor: selectedCliente.ativo ? '#ed6c02' : '#2e7d32',
                      bgcolor: alpha(selectedCliente.ativo ? '#ed6c02' : '#2e7d32', 0.04)
                    }
                  }}
                >
                  {selectedCliente.ativo ? 'Inativar' : 'Ativar'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </SwipeableDrawer>

      {/* MODAL DE CADASTRO/EDIÇÃO - FULLSCREEN MOBILE */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
            boxShadow: '0 40px 80px rgba(0,0,0,0.2)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
          p: isMobile ? 2 : 3,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2 }}>
            <Avatar sx={{
              bgcolor: 'rgba(57, 255, 20, 0.15)',
              color: '#39FF14',
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48
            }}>
              {editingId ? <EditIcon /> : <PersonAddIcon />}
            </Avatar>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: 'white', fontWeight: 700 }}>
                {editingId ? 'Editar Cliente' : 'Novo Cliente'}
              </Typography>
              {!isMobile && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {editingId ? 'Atualize as informações do cliente' : 'Preencha os dados para cadastrar'}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton 
            onClick={handleCloseDialog} 
            sx={{ 
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
                transform: 'rotate(90deg)',
                transition: 'transform 0.3s ease'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Conteúdo do Formulário */}
        <DialogContent sx={{ 
          p: isMobile ? 2 : 3,
          overflow: 'auto'
        }}>
          <Grid container spacing={isMobile ? 2 : 2.5}>
            {/* Tipo de Pessoa */}
            <Grid item xs={12}>
              <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                <InputLabel>Tipo de Pessoa</InputLabel>
                <Select
                  name="tipo_pessoa"
                  value={formData.tipo_pessoa}
                  label="Tipo de Pessoa"
                  onChange={handleInputChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="FISICA">👤 Pessoa Física</MenuItem>
                  <MenuItem value="JURIDICA">🏢 Pessoa Jurídica</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Nome/Razão Social */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label={formData.tipo_pessoa === 'FISICA' ? 'Nome Completo *' : 'Razão Social *'}
                name="nome_razao"
                value={formData.nome_razao}
                onChange={handleInputChange}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* CPF/CNPJ e Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label={formData.tipo_pessoa === 'FISICA' ? 'CPF' : 'CNPJ'}
                name="cpf_cnpj"
                value={formData.cpf_cnpj}
                onChange={handleCpfCnpjChange}
                placeholder={formData.tipo_pessoa === 'FISICA' ? '000.000.000-00' : '00.000.000/0000-00'}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Telefones */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Telefone Principal"
                name="telefone1"
                value={formData.telefone1}
                onChange={(e) => handlePhoneChange(e, 'telefone1')}
                placeholder="(00) 00000-0000"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Telefone Secundário"
                name="telefone2"
                value={formData.telefone2}
                onChange={(e) => handlePhoneChange(e, 'telefone2')}
                placeholder="(00) 00000-0000"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Endereço */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ 
                color: '#0A2647', 
                fontWeight: 600,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <LocationIcon sx={{ fontSize: 20 }} /> Endereço
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="CEP"
                name="cep"
                value={formData.cep}
                onChange={handleCepChange}
                placeholder="00000-000"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Endereço"
                name="endereco"
                value={formData.endereco}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Número"
                name="numero"
                value={formData.numero}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={8} sm={4}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Complemento"
                name="complemento"
                value={formData.complemento}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Bairro"
                name="bairro"
                value={formData.bairro}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={8} sm={4}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Cidade"
                name="cidade"
                value={formData.cidade}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="UF"
                name="estado"
                value={formData.estado}
                onChange={handleInputChange}
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Observações */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ 
                color: '#0A2647', 
                fontWeight: 600,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <DescriptionIcon sx={{ fontSize: 20 }} /> Observações
              </Typography>
              <TextField
                fullWidth
                size={isMobile ? "medium" : "small"}
                label="Observações"
                name="observacoes"
                multiline
                rows={isMobile ? 3 : 4}
                value={formData.observacoes}
                onChange={handleInputChange}
                placeholder="Adicione informações adicionais sobre o cliente..."
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                  }
                }}
              />
            </Grid>

            {/* Ativo */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.ativo}
                    onChange={handleInputChange}
                    name="ativo"
                    color="primary"
                    sx={{ '& .MuiSvgIcon-root': { fontSize: isMobile ? 24 : 24 } }}
                  />
                }
                label="Cliente Ativo"
                sx={{ color: '#0A2647' }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        {/* Footer com Botões */}
        <DialogActions sx={{ 
          p: isMobile ? 2 : 3, 
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="outlined"
            fullWidth={isMobile}
            sx={{ 
              borderRadius: 2,
              color: '#0A2647',
              borderColor: '#0A2647',
              '&:hover': {
                borderColor: '#0A2647',
                bgcolor: alpha('#0A2647', 0.04)
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            fullWidth={isMobile}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: 2,
              px: isMobile ? 0 : 4,
              bgcolor: '#0A2647',
              color: '#ffffff',
              '&:hover': { 
                bgcolor: '#1a3b5c',
                color: '#ffffff',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(10,38,71,0.3)'
              },
              '&:disabled': {
                color: '#ffffff',
                bgcolor: alpha('#0A2647', 0.6)
              },
              transition: 'all 0.3s ease'
            }}
          >
            {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Exclusão */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, id: null, nome: '' })}
        PaperProps={{
          sx: { 
            borderRadius: 4,
            p: 2,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxWidth: isMobile ? '95%' : 'auto'
          }
        }}
      >
        <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
          <Avatar sx={{
            bgcolor: 'rgba(211, 47, 47, 0.1)',
            color: '#d32f2f',
            width: isMobile ? 50 : 60,
            height: isMobile ? 50 : 60,
            mx: 'auto',
            mb: 2
          }}>
            <DeleteIcon sx={{ fontSize: isMobile ? 24 : 30 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Confirmar exclusão
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Deseja realmente excluir o cliente <strong>{deleteDialog.nome}</strong>?
            <br />
            Esta ação não pode ser desfeita.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <Button 
              onClick={() => setDeleteDialog({ open: false, id: null, nome: '' })}
              variant="outlined"
              fullWidth={isMobile}
              sx={{ 
                borderRadius: 2, 
                px: isMobile ? 0 : 4,
                color: '#0A2647',
                borderColor: '#0A2647'
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              fullWidth={isMobile}
              sx={{ 
                borderRadius: 2, 
                px: isMobile ? 0 : 4,
                color: '#ffffff',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(211,47,47,0.3)'
                }
              }}
            >
              Excluir
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Layout>
  );
}

export default Clientes;
